// Boutique — page produit refonte 2026-09 : swatches couleur, tabs taille,
// pictos "à partir de / made in France / SAV 14j / production à la demande",
// date de livraison estimée dynamique, CTA sticky mobile, galerie multi-images.
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Loader2, ArrowLeft, Truck, Sparkles, ChevronDown,
  Award, ShieldCheck, Leaf, Package, MapPin, Info,
  Layers, TrendingUp,
} from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import ProductReviewsBlock from "@/components/ProductReviewsBlock";
import CheckoutCrossSell from "@/components/CheckoutCrossSell";
import { useCurrency } from "@/context/CurrencyContext";
import { apiFetch, API } from "@/lib/api";
import { useLang } from "@/lib/i18n";
const AUTH_KEY = "geovinum.auth.v1";

// ---------------------------------------------------------------------------
// Local COPY dictionary — every visible string in FR / EN / ES / PT.
// Kept on-file (rather than global i18n) because the product-page copy is
// dense and page-scoped; translators see the whole surface at once.
// ---------------------------------------------------------------------------
const COPY = {
  fr: {
    not_found: "Carte introuvable", back_to_shop: "Retour à la boutique", back_shop_short: "Boutique",
    from_price: "à partir de", why_this_map: "Pourquoi cette carte ?",
    trust: { made_fr: "Designée en France", paper_premium: "Papier giclée premium", paper_gsm: (g) => `Papier giclée ${g} g/m²`, print_od: "Impression à la demande", ship_world: "Expédition mondiale", returns_14: "Retour 14 jours" },
    color: "Couleur", format: "Format", quantity: "Quantité", total: "Total",
    best_seller: "Best-seller", tier_applied: (l) => `${l} appliqué`,
    delivery_estimated: "Livraison estimée", delivery_note: "Production 1–2 j + transport suivi. Frais exacts calculés à l'étape suivante.",
    continue_shipping: "Continuer vers la livraison →", precta: { format: "Format", pending: "à choisir", giclee_prem: "Impression giclée premium", giclee_gsm: (g) => `Impression giclée ${g} g/m²`, ship_48: "Expédition sous 48 h", tube: "Tube rigide + suivi", ideal: "Idéal pour apprendre · décorer · offrir" },
    ship_address: "Adresse de livraison", cancel: "Annuler", billing_same: "Adresse de facturation identique à la livraison",
    billing_stripe: "Vous saisirez l'adresse de facturation à l'étape paiement (Stripe).",
    computing_ship: "Calcul de la livraison…", compute_ship: "Calculer la livraison →",
    recap: "Récapitulatif", shipping: "Livraison", ship_method: "Méthode de livraison",
    days: (a, b) => `${a}-${b} jours`, edit_address: "← Modifier l'adresse",
    redirecting: "Redirection…", pay_secure: "Payer en sécurité →",
    stripe_redirect: "Redirection vers le paiement Stripe sécurisé…",
    you_may_also_like: "Vous pourriez aussi aimer",
    upsell_tags: { same_region: "Même région", best_seller: "Best-seller", newest: "Nouveauté" },
    explorer_eyebrow: "Explorer", explorer_title: "Articles & guides du vin", explorer_sub: "Conseils, terroirs, dégustation",
    sticky_add: "Ajouter", sticky_ship: "Livraison",
    bulk: {
      start_collection: "Commencez votre collection", add_cards: (n) => `Ajoutez ${n > 1 ? `${n} cartes` : "1 carte"}`,
      save_amount: (fmt) => `économisez ${fmt}`, add1: "+1 carte",
      max_unlocked: (label) => `🎉 Palier maximum débloqué — ${label} sur toute la commande.`,
      already_saving: (fmt) => `Vous économisez déjà ${fmt} sur cette commande.`,
      free_ship_unlocked: "Livraison offerte — vous avez atteint le palier 100 €.",
      free_ship_goal: "Livraison offerte à 100 €", missing: "encore",
    },
    address: { firstName: "Prénom", lastName: "Nom", email: "Email", line1: "Adresse (modifiable)", line2: "Complément (optionnel)", city: "Ville", postCode: "Code postal", phone: "Téléphone (optionnel)" },
    accordion: {
      paper: "Papier & impression",
      paper_type: "Papier Fine Art giclée", paper_matte: "finition mate",
      paper_bullets: [
        "Encres pigmentaires archivales — tenue > 100 ans en intérieur",
        "Tailles compatibles pour l'encadrement standard IKEA / Nielsen",
      ],
      production: "Production & expédition",
      production_bullets: [
        (b) => (<><span>Impression à la demande : </span><strong>0 stock, 0 gâchis</strong></>),
        "Production 1–2 jours ouvrés en Europe, dans l'atelier le plus proche de chez vous",
        "Tube d'expédition rigide, tracking inclus, assurance jusqu'à 100 €",
      ],
      returns: "Retour & garantie 14 j",
      returns_days: "14 jours", returns_bullets_pre: "Rétractation légale de", returns_bullets_post: "après réception",
      returns_free: "Retour gratuit en France sur défaut d'impression ou avarie transport",
      returns_refund: "Remboursement sous 5 j ouvrés après réception du retour",
      returns_sav: "SAV réactif —", returns_or: "ou",
    },
  },
  en: {
    not_found: "Map not found", back_to_shop: "Back to shop", back_shop_short: "Shop",
    from_price: "from", why_this_map: "Why this map?",
    trust: { made_fr: "Designed in France", paper_premium: "Premium giclée paper", paper_gsm: (g) => `Giclée paper ${g} gsm`, print_od: "Print on demand", ship_world: "Worldwide shipping", returns_14: "14-day returns" },
    color: "Colour", format: "Size", quantity: "Quantity", total: "Total",
    best_seller: "Best-seller", tier_applied: (l) => `${l} applied`,
    delivery_estimated: "Estimated delivery", delivery_note: "Production 1–2 days + tracked transport. Exact fees calculated on the next step.",
    continue_shipping: "Continue to shipping →", precta: { format: "Size", pending: "to be picked", giclee_prem: "Premium giclée print", giclee_gsm: (g) => `Giclée print ${g} gsm`, ship_48: "Ships within 48 h", tube: "Rigid tube + tracking", ideal: "Perfect to learn · decorate · gift" },
    ship_address: "Shipping address", cancel: "Cancel", billing_same: "Billing address same as shipping",
    billing_stripe: "You'll enter the billing address on the Stripe payment step.",
    computing_ship: "Computing shipping…", compute_ship: "Compute shipping →",
    recap: "Summary", shipping: "Shipping", ship_method: "Shipping method",
    days: (a, b) => `${a}-${b} days`, edit_address: "← Edit address",
    redirecting: "Redirecting…", pay_secure: "Pay securely →",
    stripe_redirect: "Redirecting to secure Stripe payment…",
    you_may_also_like: "You may also like",
    upsell_tags: { same_region: "Same region", best_seller: "Best-seller", newest: "New" },
    explorer_eyebrow: "Explore", explorer_title: "Wine articles & guides", explorer_sub: "Tips, terroirs, tastings",
    sticky_add: "Add", sticky_ship: "Shipping",
    bulk: {
      start_collection: "Start your collection", add_cards: (n) => `Add ${n > 1 ? `${n} maps` : "1 map"}`,
      save_amount: (fmt) => `save ${fmt}`, add1: "+1 map",
      max_unlocked: (label) => `🎉 Top tier unlocked — ${label} across the order.`,
      already_saving: (fmt) => `You're already saving ${fmt} on this order.`,
      free_ship_unlocked: "Free shipping — you reached the €100 threshold.",
      free_ship_goal: "Free shipping over €100", missing: "still",
    },
    address: { firstName: "First name", lastName: "Last name", email: "Email", line1: "Address (editable)", line2: "Complement (optional)", city: "City", postCode: "Postcode", phone: "Phone (optional)" },
    accordion: {
      paper: "Paper & printing", paper_type: "Fine Art giclée paper", paper_matte: "matte finish",
      paper_bullets: ["Archival pigment inks — 100+ years indoor lifespan", "Sizes compatible with standard IKEA / Nielsen frames"],
      production: "Production & shipping",
      production_bullets: [(b)=>(<><span>Print on demand: </span><strong>zero stock, zero waste</strong></>), "Production 1–2 business days in Europe, at the workshop nearest you", "Rigid shipping tube, tracking included, insured up to €100"],
      returns: "Returns & 14-day warranty",
      returns_days: "14 days", returns_bullets_pre: "Legal withdrawal period of", returns_bullets_post: "after receipt",
      returns_free: "Free returns in France on print defect or transport damage",
      returns_refund: "Refund within 5 business days after return receipt",
      returns_sav: "Responsive customer care —", returns_or: "or",
    },
  },
  es: {
    not_found: "Mapa no encontrado", back_to_shop: "Volver a la tienda", back_shop_short: "Tienda",
    from_price: "desde", why_this_map: "¿Por qué este mapa?",
    trust: { made_fr: "Diseñado en Francia", paper_premium: "Papel giclée premium", paper_gsm: (g) => `Papel giclée ${g} g/m²`, print_od: "Impresión a demanda", ship_world: "Envío mundial", returns_14: "Devolución 14 días" },
    color: "Color", format: "Tamaño", quantity: "Cantidad", total: "Total",
    best_seller: "Más vendido", tier_applied: (l) => `${l} aplicado`,
    delivery_estimated: "Entrega estimada", delivery_note: "Producción 1–2 d + transporte con seguimiento. Gastos exactos calculados en el siguiente paso.",
    continue_shipping: "Continuar al envío →", precta: { format: "Tamaño", pending: "por elegir", giclee_prem: "Impresión giclée premium", giclee_gsm: (g) => `Impresión giclée ${g} g/m²`, ship_48: "Envío en 48 h", tube: "Tubo rígido + seguimiento", ideal: "Ideal para aprender · decorar · regalar" },
    ship_address: "Dirección de envío", cancel: "Cancelar", billing_same: "Dirección de facturación igual que la de envío",
    billing_stripe: "Introducirás la dirección de facturación en el paso de pago (Stripe).",
    computing_ship: "Calculando envío…", compute_ship: "Calcular el envío →",
    recap: "Resumen", shipping: "Envío", ship_method: "Método de envío",
    days: (a, b) => `${a}-${b} días`, edit_address: "← Editar dirección",
    redirecting: "Redirigiendo…", pay_secure: "Pagar de forma segura →",
    stripe_redirect: "Redirigiendo al pago seguro Stripe…",
    you_may_also_like: "También te podría gustar",
    upsell_tags: { same_region: "Misma región", best_seller: "Más vendido", newest: "Novedad" },
    explorer_eyebrow: "Explorar", explorer_title: "Artículos y guías del vino", explorer_sub: "Consejos, terroirs, catas",
    sticky_add: "Añadir", sticky_ship: "Envío",
    bulk: {
      start_collection: "Empieza tu colección", add_cards: (n) => `Añade ${n > 1 ? `${n} mapas` : "1 mapa"}`,
      save_amount: (fmt) => `ahorra ${fmt}`, add1: "+1 mapa",
      max_unlocked: (label) => `🎉 Tope máximo desbloqueado — ${label} en todo el pedido.`,
      already_saving: (fmt) => `Ya estás ahorrando ${fmt} en este pedido.`,
      free_ship_unlocked: "Envío gratuito — has alcanzado el umbral de 100 €.",
      free_ship_goal: "Envío gratuito a partir de 100 €", missing: "faltan",
    },
    address: { firstName: "Nombre", lastName: "Apellidos", email: "Email", line1: "Dirección (editable)", line2: "Complemento (opcional)", city: "Ciudad", postCode: "Código postal", phone: "Teléfono (opcional)" },
    accordion: {
      paper: "Papel e impresión", paper_type: "Papel Fine Art giclée", paper_matte: "acabado mate",
      paper_bullets: ["Tintas pigmentadas de archivo — más de 100 años en interior", "Tamaños compatibles con marcos estándar IKEA / Nielsen"],
      production: "Producción y envío",
      production_bullets: [(b)=>(<><span>Impresión a demanda: </span><strong>cero stock, cero desperdicio</strong></>), "Producción 1–2 días hábiles en Europa, en el taller más cercano a ti", "Tubo rígido de envío, tracking incluido, seguro hasta 100 €"],
      returns: "Devolución y garantía 14 d",
      returns_days: "14 días", returns_bullets_pre: "Derecho legal de desistimiento de", returns_bullets_post: "tras la recepción",
      returns_free: "Devolución gratuita en Francia por defecto de impresión o daño de transporte",
      returns_refund: "Reembolso en 5 días hábiles tras recibir la devolución",
      returns_sav: "SAV reactivo —", returns_or: "o",
    },
  },
  pt: {
    not_found: "Mapa não encontrado", back_to_shop: "Voltar à loja", back_shop_short: "Loja",
    from_price: "a partir de", why_this_map: "Porquê este mapa?",
    trust: { made_fr: "Desenhado em França", paper_premium: "Papel giclée premium", paper_gsm: (g) => `Papel giclée ${g} g/m²`, print_od: "Impressão a pedido", ship_world: "Envio mundial", returns_14: "Devolução 14 dias" },
    color: "Cor", format: "Tamanho", quantity: "Quantidade", total: "Total",
    best_seller: "Mais vendido", tier_applied: (l) => `${l} aplicado`,
    delivery_estimated: "Entrega estimada", delivery_note: "Produção 1–2 d + transporte com seguimento. Custos exatos calculados no passo seguinte.",
    continue_shipping: "Continuar para envio →", precta: { format: "Tamanho", pending: "a escolher", giclee_prem: "Impressão giclée premium", giclee_gsm: (g) => `Impressão giclée ${g} g/m²`, ship_48: "Envio em 48 h", tube: "Tubo rígido + seguimento", ideal: "Ideal para aprender · decorar · oferecer" },
    ship_address: "Morada de entrega", cancel: "Cancelar", billing_same: "Morada de faturação igual à de entrega",
    billing_stripe: "Introduzirá a morada de faturação no passo de pagamento (Stripe).",
    computing_ship: "A calcular o envio…", compute_ship: "Calcular o envio →",
    recap: "Resumo", shipping: "Envio", ship_method: "Método de envio",
    days: (a, b) => `${a}-${b} dias`, edit_address: "← Editar morada",
    redirecting: "A redirecionar…", pay_secure: "Pagar em segurança →",
    stripe_redirect: "A redirecionar para o pagamento seguro Stripe…",
    you_may_also_like: "Também pode gostar",
    upsell_tags: { same_region: "Mesma região", best_seller: "Mais vendido", newest: "Novidade" },
    explorer_eyebrow: "Explorar", explorer_title: "Artigos e guias do vinho", explorer_sub: "Conselhos, terroirs, provas",
    sticky_add: "Adicionar", sticky_ship: "Envio",
    bulk: {
      start_collection: "Comece a sua coleção", add_cards: (n) => `Adicione ${n > 1 ? `${n} mapas` : "1 mapa"}`,
      save_amount: (fmt) => `poupa ${fmt}`, add1: "+1 mapa",
      max_unlocked: (label) => `🎉 Patamar máximo desbloqueado — ${label} em toda a encomenda.`,
      already_saving: (fmt) => `Já está a poupar ${fmt} nesta encomenda.`,
      free_ship_unlocked: "Envio grátis — atingiu o limite de 100 €.",
      free_ship_goal: "Envio grátis a partir de 100 €", missing: "ainda",
    },
    address: { firstName: "Nome", lastName: "Apelido", email: "Email", line1: "Morada (editável)", line2: "Complemento (opcional)", city: "Cidade", postCode: "Código postal", phone: "Telefone (opcional)" },
    accordion: {
      paper: "Papel e impressão", paper_type: "Papel Fine Art giclée", paper_matte: "acabamento mate",
      paper_bullets: ["Tintas pigmentadas de arquivo — mais de 100 anos em interior", "Tamanhos compatíveis com molduras padrão IKEA / Nielsen"],
      production: "Produção e envio",
      production_bullets: [(b)=>(<><span>Impressão a pedido: </span><strong>zero stock, zero desperdício</strong></>), "Produção 1–2 dias úteis na Europa, no atelier mais próximo de si", "Tubo de envio rígido, tracking incluído, seguro até 100 €"],
      returns: "Devolução e garantia 14 d",
      returns_days: "14 dias", returns_bullets_pre: "Direito legal de retratação de", returns_bullets_post: "após a receção",
      returns_free: "Devolução grátis em França por defeito de impressão ou dano de transporte",
      returns_refund: "Reembolso em 5 dias úteis após receção da devolução",
      returns_sav: "SAV reativo —", returns_or: "ou",
    },
  },
};

const useCopy = () => {
  const lang = useLang() || "fr";
  return COPY[lang] || COPY.fr;
};

function authHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const t = parsed?.token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
}

const COUNTRIES = [
  ["FR", "France"], ["BE", "Belgique"], ["CH", "Suisse"], ["LU", "Luxembourg"], ["DE", "Allemagne"],
  ["ES", "Espagne"], ["IT", "Italie"], ["GB", "Royaume-Uni"], ["NL", "Pays-Bas"], ["PT", "Portugal"],
  ["AT", "Autriche"], ["IE", "Irlande"], ["SE", "Suède"], ["DK", "Danemark"], ["NO", "Norvège"],
  ["FI", "Finlande"], ["PL", "Pologne"], ["US", "États-Unis"], ["CA", "Canada"],
];

const blankAddress = {
  firstName: "", lastName: "", addressLine1: "", addressLine2: "",
  city: "", postCode: "", country: "FR", state: "", email: "", phone: "", companyName: "",
};

// ─── Colour swatch palette ─────────────────────────────────────────────
// Fixed hex mapping for the 3 canonical geoVINUM finishes (Vintage /
// Night / Modern). Any unknown value falls back to a neutral swatch so
// new colours added upstream still render — just without a bespoke chip.
const COLOR_SWATCHES = {
  vintage: { bg: "#c9a476", ring: "#8f6f45", label: "Vintage" },
  night:   { bg: "#171d2c", ring: "#0a0e18", label: "Night" },
  modern:  { bg: "#f4f0ea", ring: "#c8bdae", label: "Modern" },
};
const swatchFor = (color) => COLOR_SWATCHES[String(color || "").toLowerCase()] || {
  bg: "#dedede", ring: "#a8a8a8", label: color || "—",
};

// Shipping baseline used by the "Livrée entre X et Y" callout. Values
// match the /api/shop/google-merchant.xml <g:shipping> feed exactly so
// the promise on the product page never contradicts what Google Shopping
// shows in the ad snippet.
const SHIPPING_BAND = {
  FR: { min: 5, max: 9,  label: "France" },
  BE: { min: 6, max: 10, label: "Belgique" },
  DE: { min: 6, max: 10, label: "Allemagne" },
  ES: { min: 6, max: 10, label: "Espagne" },
  IT: { min: 6, max: 10, label: "Italie" },
  NL: { min: 6, max: 10, label: "Pays-Bas" },
  PT: { min: 6, max: 10, label: "Portugal" },
  LU: { min: 6, max: 10, label: "Luxembourg" },
  AT: { min: 6, max: 10, label: "Autriche" },
  IE: { min: 6, max: 10, label: "Irlande" },
  GB: { min: 7, max: 12, label: "Royaume-Uni" },
  CH: { min: 7, max: 12, label: "Suisse" },
  US: { min: 8, max: 14, label: "États-Unis" },
  CA: { min: 10, max: 16, label: "Canada" },
};

// Gelato production time on posters (fine art paper) — always 1-2 open
// days before shipping. We add this to the transit band so the "livrée
// entre le X et Y" wording matches the Fulfilment SLA sent to Gelato.
const PRODUCTION_MIN_DAYS = 1;
const PRODUCTION_MAX_DAYS = 2;

const formatDeliveryBand = (country) => {
  const band = SHIPPING_BAND[country] || SHIPPING_BAND.FR;
  const now = new Date();
  const minDate = new Date(now);
  minDate.setDate(minDate.getDate() + PRODUCTION_MIN_DAYS + band.min);
  const maxDate = new Date(now);
  maxDate.setDate(maxDate.getDate() + PRODUCTION_MAX_DAYS + band.max);
  const opts = { day: "numeric", month: "long" };
  return {
    country: band.label,
    range: `${minDate.toLocaleDateString("fr-FR", opts)} — ${maxDate.toLocaleDateString("fr-FR", opts)}`,
  };
};

// ─── Bulk discount ladder ────────────────────────────────────────────
// Encourages multi-card orders from the first click. Applied client-side
// on the total shown next to the quantity; the actual discount is passed
// to Gelato as a checkout adjustment. Tier boundaries chosen so the very
// first upsell (2 cartes = -10 %) is reachable in one click, and the
// bigger jump (5 = -20 %) makes a "collection" feel budgeted.
// Free-shipping threshold — the "livraison offerte dès X €" nudge is a
// major micro-conversion lever in eCommerce. Applies to any single-line
// order; when the discounted total reaches this floor we surface an
// "unlocked" state, otherwise a progress bar shows the missing amount.
const FREE_SHIPPING_THRESHOLD_CENTS = 10000; // 100 € (EUR)

const BULK_TIERS = [
  { min: 5, pct: 20, label: "-20 %", pitch: "5 cartes ou plus" },
  { min: 3, pct: 15, label: "-15 %", pitch: "3 cartes" },
  { min: 2, pct: 10, label: "-10 %", pitch: "2 cartes" },
];

/** Return the best tier that applies to `qty`, or null. */
const currentBulkTier = (qty) => BULK_TIERS.find((t) => qty >= t.min) || null;

/** Return the next tier above `qty` (upsell target), or null when maxed. */
const nextBulkTier = (qty) => {
  // BULK_TIERS is sorted desc by `min`, so scan bottom-up to find the
  // smallest tier the user hasn't yet unlocked.
  for (let i = BULK_TIERS.length - 1; i >= 0; i -= 1) {
    if (BULK_TIERS[i].min > qty) return BULK_TIERS[i];
  }
  return null;
};

const ShopProductPage = () => {
  const c = useCopy();
  const { slug: productId } = useParams();
  const { currency, formatFromEUR, formatCents } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Variant selection — split by color + size dropdowns
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [quantity, setQuantity] = useState(1);

  // Image gallery
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [mockups, setMockups] = useState([]);

  // Checkout state — address form hidden by default
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [step, setStep] = useState(1);
  const [shipAddress, setShipAddress] = useState(blankAddress);
  const [billingSame, setBillingSame] = useState(true);
  const [quote, setQuote] = useState(null);
  const [chosenMethod, setChosenMethod] = useState("");
  const [quoting, setQuoting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  // Upsell
  const [upsells, setUpsells] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      const { ok, data: d, error: err } = await apiFetch(`/api/shop/products/${productId}`, { headers: authHeaders() });
      if (cancelled) return;
      if (!ok) {
        setError(err || "Request failed");
      } else {
        setData(d);
        const first = d.variants?.[0];
        if (first) { setColor(first.color || ""); setSize(first.size || ""); }
      }
      setLoading(false);
    };
    fetchData();
    return () => { cancelled = true; };
  }, [productId]);

  // Fetch Gelato mockup gallery (20-40 shots vs 3 stored Etsy thumbs).
  // Endpoint is cached 12h server-side so re-visiting doesn't pay Gelato.
  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    (async () => {
      const { ok, data: d } = await apiFetch(`/api/shop/products/${productId}/mockups`);
      if (!cancelled && ok) setMockups(d?.mockups || []);
    })();
    return () => { cancelled = true; };
  }, [productId]);

  // Fetch upsell suggestions — mix of 1 same region + 1 best-seller + 1 newest
  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    (async () => {
      const regionParam = data.region_slug && data.region_slug !== "other" ? data.region_slug : null;
      const [sameRegionRes, bestSellersRes, newestRes] = await Promise.all([
        regionParam
          ? apiFetch(`/api/shop/products?region=${regionParam}&sort=best_sellers&limit=12`)
          : Promise.resolve({ ok: true, data: { items: [] } }),
        apiFetch(`/api/shop/products?sort=best_sellers&limit=12`),
        apiFetch(`/api/shop/products?sort=newest&limit=12`),
      ]);
      if (cancelled) return;
      const sameRegion = sameRegionRes.ok ? sameRegionRes.data : { items: [] };
      const bestSellers = bestSellersRes.ok ? bestSellersRes.data : { items: [] };
      const newest = newestRes.ok ? newestRes.data : { items: [] };
      const used = new Set([productId]);
      const pick = (list, tag) => {
        const found = (list.items || []).find((p) => !used.has(p.product_id));
        if (found) { used.add(found.product_id); return { ...found, _tag: tag }; }
        return null;
      };
      const out = [];
      const sr = pick(sameRegion, c.upsell_tags.same_region);
      if (sr) out.push(sr);
      const bs = pick(bestSellers, c.upsell_tags.best_seller);
      if (bs) out.push(bs);
      const nw = pick(newest, c.upsell_tags.newest);
      if (nw) out.push(nw);
      // If we don't have 3 yet (small catalog or excluded), fill from best-sellers
      for (const p of bestSellers.items || []) {
        if (out.length >= 3) break;
        if (!used.has(p.product_id)) { used.add(p.product_id); out.push({ ...p, _tag: c.upsell_tags.best_seller }); }
      }
      setUpsells(out.slice(0, 3));
    })();
    return () => { cancelled = true; };
  }, [data, productId]);

  // Available colors & sizes from variants
  const colors = useMemo(() => {
    if (!data?.variants) return [];
    const set = new Set();
    data.variants.forEach((v) => v.color && set.add(v.color));
    return Array.from(set);
  }, [data]);

  const sizes = useMemo(() => {
    if (!data?.variants) return [];
    const set = new Set();
    data.variants.forEach((v) => v.size && set.add(v.size));
    return Array.from(set);
  }, [data]);

  // Resolve current variant_id from color + size combo
  const selectedVariant = useMemo(() => {
    if (!data?.variants) return null;
    return data.variants.find((v) =>
      (!color || v.color === color) && (!size || v.size === size),
    );
  }, [data, color, size]);

  // "À partir de" — lowest variant price. Kept independent of the current
  // selection so the badge stays stable while the user is still choosing.
  const fromCents = useMemo(() => {
    if (!data?.variants?.length) return null;
    return data.variants.reduce(
      (m, v) => (v.amount_cents && v.amount_cents < m ? v.amount_cents : m),
      Number.POSITIVE_INFINITY,
    );
  }, [data]);

  // The user's country is only known once they've entered an address —
  // before that we default to FR (85% of traffic). Once the address form
  // has a country the band updates automatically.
  const deliveryCountry = shipAddress?.country || "FR";
  const deliveryEstimate = useMemo(() => formatDeliveryBand(deliveryCountry), [deliveryCountry]);

  // Build gallery — Gelato's `productImages` (10-40 URLs) is the primary
  // source. We dedupe variant-specific mockups **by colour** (keeping just
  // one representative per style — Modern / Vintage / Night — rather than
  // 3 sizes × 3 styles = 9 near-identical renders). Lifestyle shots
  // (variant_ids empty) are kept in full.
  //
  // Ordering, for the selected variant:
  //   1. The mockup matching the current colour (variant-tagged)
  //   2. Primary product hero
  //   3. Every lifestyle mockup (framed on wall, in-hand, etc.)
  //   4. Other colours' variant mockups (so users can compare styles)
  //   5. Etsy fallback only when Gelato returned zero mockups
  const gallery = useMemo(() => {
    if (!data) return [];
    const items = [];
    const seen = new Set();
    const variantById = new Map();
    (data.variants || []).forEach((v) => {
      if (v.variant_id) variantById.set(v.variant_id, v);
    });
    const push = (url, altSuffix) => {
      if (!url || seen.has(url)) return;
      seen.add(url);
      const alt = altSuffix ? `${data.title} — ${altSuffix}` : data.title;
      items.push({ url, alt });
    };
    // For a variant-tagged mockup, return the underlying variant object so
    // we can extract colour / size for alt text and colour-based dedup.
    const variantOf = (m) => {
      const vid = (m.variant_ids || [])[0];
      return vid ? variantById.get(vid) : null;
    };
    // Dedupe variant mockups keeping one per colour. Prefer the current
    // selected size when possible (so switching size doesn't reshuffle the
    // gallery for no reason), otherwise the first mockup encountered wins.
    const byColour = new Map(); // colour -> mockup (representative)
    (mockups || []).forEach((m) => {
      if (m.is_primary || !(m.variant_ids || []).length) return;
      const v = variantOf(m);
      if (!v || !v.color) return;
      const existing = byColour.get(v.color);
      if (!existing) { byColour.set(v.color, m); return; }
      // Replace if this mockup matches the currently-selected size — that
      // way the hero always shows the variant the user actually configured.
      const existingV = variantOf(existing);
      if (size && v.size === size && existingV?.size !== size) byColour.set(v.color, m);
    });

    // 1. Selected colour first
    const currentEntry = color ? byColour.get(color) : null;
    if (currentEntry) {
      const v = variantOf(currentEntry);
      push(currentEntry.url, [v?.color, v?.size].filter(Boolean).join(" · "));
    }
    // 2. Primary
    (mockups || []).filter((m) => m.is_primary).forEach((m) => push(m.url, ""));
    // 3. Remaining colours (one per colour) — surfaced BEFORE lifestyle
    //    shots so the 3 style options (Modern / Vintage / Night) are
    //    always visible above the fold and never pushed off by mockups.
    for (const [c, m] of byColour) {
      if (c === color) continue;
      const v = variantOf(m);
      push(m.url, [v?.color, v?.size].filter(Boolean).join(" · "));
    }
    // 4. Lifestyle mockups (variant-agnostic scenes — framed on wall, etc.)
    (mockups || [])
      .filter((m) => !m.is_primary && !(m.variant_ids || []).length)
      .forEach((m) => push(m.url, ""));

    if (items.length === 0) {
      push(`${API}/api/shop/preview/${data.product_id}`, "");
      push(data.external_preview_url, "");
      (data.gallery_urls || []).forEach((u) => push(u, ""));
    }
    return items.slice(0, 20);
  }, [data, mockups, selectedVariant, color, size]);

  // Whenever the user picks a different colour, snap the hero image to the
  // gallery slot that carries that colour so the preview matches selection
  // instantly. `gallery[0]` is already the selected-colour mockup by the
  // memo above, so we just reset the active index.
  useEffect(() => {
    setActiveImageIdx(0);
  }, [color]);

  const handleQuote = async (e) => {
    e?.preventDefault();
    if (!selectedVariant) return;
    setCheckoutError(null); setQuoting(true); setQuote(null);
    const { ok, data: d, error: err } = await apiFetch(`/api/prints/quote`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        product_id: productId,
        variant_id: selectedVariant.variant_id,
        quantity,
        recipient: shipAddress,
        currency,
      }),
    });
    if (!ok) {
      setCheckoutError(err || "Impossible de calculer la livraison.");
    } else {
      setQuote(d);
      if (d.shipping_options?.length) setChosenMethod(d.shipping_options[0].method_uid);
      setStep(2);
    }
    setQuoting(false);
  };

  const handlePay = async () => {
    if (!quote || !chosenMethod || !selectedVariant) return;
    setPaying(true); setCheckoutError(null);
    const ship = quote.shipping_options.find((o) => o.method_uid === chosenMethod);
    const { ok, data: d, error: err } = await apiFetch(`/api/prints/checkout-v2`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        product_id: productId, variant_id: selectedVariant.variant_id, quantity,
        shipping_method_uid: chosenMethod,
        shipping_amount_cents: ship.price_cents,
        recipient: shipAddress,
        billing_same_as_shipping: billingSame,
        origin_url: window.location.origin, lang: "fr",
        currency,
      }),
    });
    if (!ok) {
      setCheckoutError(err || "Paiement impossible.");
    } else {
      setStep(3); window.location.href = d.url;
    }
    setPaying(false);
  };

  if (loading) {
    return <Layout testId="page-shop-product-loading"><div className="min-h-[70vh] flex items-center justify-center"><Loader2 size={24} className="animate-spin text-[color:var(--gv-pink)]" /></div></Layout>;
  }
  if (error || !data) {
    return <Layout testId="page-shop-product-error"><div className="min-h-[60vh] flex items-center justify-center px-6"><div className="text-center max-w-md"><div className="font-display text-2xl text-[color:var(--gv-ink)] mb-3">{c.not_found}</div><Link to="/shop" className="btn-primary">{c.back_to_shop} <ArrowLeft size={14} className="rotate-180" /></Link></div></div></Layout>;
  }

  return (
    <Layout testId="page-shop-product">
      <SEO title={`${data.title} | geoVINUM`} description={data.description?.slice(0, 160)} />

      <section className="pt-24 md:pt-32 pb-12 bg-[color:var(--gv-mist)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <Link to="/shop" className="inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-pink)] mb-6 transition-colors" data-testid="shop-product-back">
            <ArrowLeft size={14} /> {c.back_shop_short}
          </Link>

          <div className="grid lg:grid-cols-5 gap-10">
            {/* Gallery */}
            <div className="lg:col-span-2 space-y-3">
              <div className="aspect-square bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden flex items-center justify-center">
                <img src={gallery[activeImageIdx]?.url || data.preview}
                     alt={gallery[activeImageIdx]?.alt || data.title}
                     className="w-full h-full object-contain" data-testid="shop-product-image" />
              </div>
              {gallery.length > 1 && (
                <div className="grid grid-cols-5 gap-2" data-testid="shop-product-thumbs">
                  {gallery.map((item, idx) => (
                    <button key={item.url} onClick={() => setActiveImageIdx(idx)}
                            className={`aspect-square border rounded-[5px] overflow-hidden bg-white transition-all ${activeImageIdx === idx ? "border-[color:var(--gv-pink)] ring-1 ring-[color:var(--gv-pink)]/30" : "border-[color:var(--gv-line)] hover:border-[color:var(--gv-ink)]/30"}`}
                            aria-label={item.alt}
                            data-testid={`shop-thumb-${idx}`}>
                      <img src={item.url} alt={item.alt} className="w-full h-full object-contain" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Buy panel */}
            <div className="lg:col-span-3">
              <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] leading-[1.1] mb-2" data-testid="shop-product-title">{data.title}</h1>

              {/* Editorial badge — same source as the listing card, rendered
                  as a chip line just under the title for consistency. */}
              {data.meta?.badge_meta && (
                <div className="mb-3">
                  <span
                    className="inline-flex items-center gap-1.5 bg-[color:var(--gv-ink)] text-white text-[11px] font-medium uppercase tracking-wider px-2.5 py-1 rounded-[5px]"
                    data-testid="shop-product-badge"
                  >
                    <span aria-hidden>{data.meta.badge_meta.emoji}</span>{data.meta.badge_meta.label}
                  </span>
                </div>
              )}

              {/* Price row : selected price + "à partir de" fallback for sanity */}
              <div className="flex items-baseline gap-3 mb-4">
                {selectedVariant ? (
                  <>
                    <div className="font-display text-2xl text-[color:var(--gv-pink)]" data-testid="shop-product-price">
                      {formatFromEUR(selectedVariant.amount_cents)}
                    </div>
                    {fromCents != null && fromCents < selectedVariant.amount_cents && (
                      <div className="text-[12px] text-[color:var(--gv-ink)]/50">
                        {c.from_price} {formatFromEUR(fromCents)}
                      </div>
                    )}
                  </>
                ) : fromCents != null ? (
                  <div className="font-display text-2xl text-[color:var(--gv-ink)]" data-testid="shop-product-price-from">
                    {c.from_price} {formatFromEUR(fromCents)}
                  </div>
                ) : null}
              </div>

              {/* "Pourquoi cette carte ?" — editorial answer to the silent
                  question "why this one and not another?". Only rendered
                  when the admin has filled at least one bullet; otherwise
                  we let the pictos + accordion do the reassurance work. */}
              {data.meta?.why_bullets?.length > 0 && (
                <div className="mb-6 p-4 bg-[color:var(--gv-pink)]/5 border border-[color:var(--gv-pink)]/15 rounded-[5px]" data-testid="shop-product-why">
                  <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-2.5 font-medium">
                    {c.why_this_map}
                  </div>
                  <ul className="space-y-1.5">
                    {data.meta.why_bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13.5px] text-[color:var(--gv-ink)]/85 leading-snug">
                        <span className="text-[color:var(--gv-pink)] font-semibold shrink-0 mt-0.5">✓</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Trust pictos — always visible, straight below the price so the
                  main selling arguments are absorbed before the fold. Kept
                  minimal (5 items) — a wall of icons kills the premium feel.
                  Paper weight is pulled from the selected variant's Gelato
                  spec so what we advertise stays truthful when the visitor
                  switches formats. */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6" data-testid="shop-trust-pictos">
                {[
                  { Icon: MapPin,      label: c.trust.made_fr },
                  { Icon: Leaf,        label: selectedVariant?.paper_spec?.weight_gsm
                                              ? c.trust.paper_gsm(selectedVariant.paper_spec.weight_gsm)
                                              : c.trust.paper_premium },
                  { Icon: Package,     label: c.trust.print_od },
                  { Icon: Truck,       label: c.trust.ship_world },
                  { Icon: ShieldCheck, label: c.trust.returns_14 },
                ].map(({ Icon, label }) => (
                  <div key={label} className="flex flex-col items-center text-center gap-1.5 px-2 py-3 bg-white border border-[color:var(--gv-line)] rounded-[5px]">
                    <Icon size={16} className="text-[color:var(--gv-pink)]" />
                    <div className="text-[10.5px] leading-tight text-[color:var(--gv-ink)]/75">{label}</div>
                  </div>
                ))}
              </div>

              <p className="text-[14px] text-[color:var(--gv-ink)]/75 leading-relaxed mb-6 whitespace-pre-line" dangerouslySetInnerHTML={{ __html: (data.description || "").replace(/<\/?(html|body|head|meta|link|script|style)[^>]*>/gi, "").slice(0, 1500) }} />

              {step === 1 && (
                <div className="space-y-6" data-testid="checkout-step-1">
                  {/* Colour swatches — visual chips beat a dropdown for a
                      3-value axis, and match the finish's actual paper tone. */}
                  {colors.length > 1 && (
                    <div>
                      <div className="flex items-baseline justify-between mb-2">
                        <Label className="!mb-0">{c.color}</Label>
                        <span className="text-[12px] text-[color:var(--gv-ink)]/60" data-testid="selected-color">{color || "—"}</span>
                      </div>
                      <div className="flex items-center gap-2.5" role="radiogroup" aria-label="Couleur">
                        {colors.map((c) => {
                          const sw = swatchFor(c);
                          const active = color === c;
                          return (
                            <button
                              key={c}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              onClick={() => setColor(c)}
                              title={sw.label}
                              className={`w-11 h-11 rounded-full border-2 border-white transition-all ${active ? "ring-[3px] ring-offset-[3px] ring-[color:var(--gv-pink)] scale-105 shadow-[0_4px_12px_-2px_rgba(209,25,124,0.35)]" : "ring-1 ring-offset-1 ring-[color:var(--gv-line)] hover:scale-105 hover:ring-[color:var(--gv-ink)]/30"}`}
                              style={{ backgroundColor: sw.bg, boxShadow: `inset 0 0 0 1px ${sw.ring}` }}
                              data-testid={`color-swatch-${c}`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Size tabs — horizontal segmented control. Reads faster
                      than a dropdown and shows all 3 options at a glance. */}
                  {sizes.length > 1 && (
                    <div>
                      <div className="flex items-baseline justify-between mb-2">
                        <Label className="!mb-0">{c.format}</Label>
                        <span className="text-[12px] text-[color:var(--gv-ink)]/60" data-testid="selected-size">{size || "—"}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Format">
                        {sizes.map((s) => {
                          // Match the variant carrying this size for the current colour
                          // so each tab surfaces its own price (helps the user compare
                          // 30×40 → 70×100 without clicking through).
                          const priced = data.variants?.find(
                            (v) => v.size === s && (!color || v.color === color),
                          );
                          const active = size === s;
                          // Data-driven best-seller flag — the field is populated by
                          // /shop/products/{id} based on the last 90 days of orders.
                          // Falls back to the "50 x 70 cm" sweet spot when we don't
                          // yet have enough order signal to compute the top size.
                          const isBestSeller = data.best_seller_size
                            ? s === data.best_seller_size
                            : /50\s*[x×]\s*70/i.test(s);
                          return (
                            <button
                              key={s}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              onClick={() => setSize(s)}
                              className={`relative text-center px-3 py-2.5 rounded-[5px] border transition-colors ${active ? "border-[color:var(--gv-pink)] bg-[color:var(--gv-pink)]/5 text-[color:var(--gv-ink)]" : "border-[color:var(--gv-line)] hover:border-[color:var(--gv-ink)]/30 text-[color:var(--gv-ink)]/80"}`}
                              data-testid={`size-tab-${s}`}
                            >
                              {isBestSeller && (
                                <span
                                  className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-[3px] bg-[color:var(--gv-pink)] text-white text-[9px] uppercase tracking-widest font-medium whitespace-nowrap shadow-sm"
                                  data-testid={`size-tab-badge-${s}`}
                                >
                                  ★ {c.best_seller}
                                </span>
                              )}
                              <div className="text-[12.5px] font-medium leading-tight">{s}</div>
                              {priced?.amount_cents ? (
                                <div className="text-[11px] text-[color:var(--gv-ink)]/55 mt-0.5">{formatFromEUR(priced.amount_cents)}</div>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex items-end gap-4">
                    <div>
                      <Label>{c.quantity}</Label>
                      <input type="number" min={1} max={20} value={quantity}
                             onChange={(e) => setQuantity(parseInt(e.target.value || "1", 10))}
                             className="gv-input w-24" data-testid="quantity-input" />
                    </div>
                    {selectedVariant?.amount_cents ? (
                      <div className="flex-1 text-right">
                        <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-0.5">{c.total}</div>
                        <div className="font-display text-xl text-[color:var(--gv-ink)] leading-none" data-testid="bulk-total">
                          {(() => {
                            const tier = currentBulkTier(quantity);
                            const base = selectedVariant.amount_cents * quantity;
                            const finalCents = tier ? Math.round(base * (1 - tier.pct / 100)) : base;
                            return formatFromEUR(finalCents);
                          })()}
                        </div>
                        {currentBulkTier(quantity) && (
                          <div className="text-[11px] text-green-600 mt-0.5" data-testid="bulk-savings">
                            {c.tier_applied(currentBulkTier(quantity).label)}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>

                  {/* Bulk-order incentive — surfaces the discount ladder from
                      the very first card. Two states:
                      - upsell chip when the user hasn't hit the next tier
                        yet ("Ajoutez 1 carte pour -10 %")
                      - unlocked chip when they have ("Palier -15 % débloqué")
                      Encourages horizontal navigation via the "collection"
                      link below the chip. */}
                  <BulkIncentive
                    quantity={quantity}
                    unitCents={selectedVariant?.amount_cents || 0}
                    onIncrement={() => setQuantity((q) => Math.min(20, q + 1))}
                  />

                  {/* Delivery estimate — visible before the user opens the
                      full address form, so the shipping promise is part of
                      the decision. Country falls back to FR until the form
                      is filled. */}
                  <div className="flex items-start gap-3 p-3 bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] rounded-[5px]" data-testid="delivery-estimate">
                    <Truck size={16} className="text-[color:var(--gv-pink)] mt-0.5 shrink-0" />
                    <div className="text-[13px] leading-snug text-[color:var(--gv-ink)]/85">
                      {c.delivery_estimated} <strong>{deliveryEstimate.range}</strong>
                      <span className="text-[color:var(--gv-ink)]/55"> · {deliveryEstimate.country}</span>
                      <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 mt-0.5">{c.delivery_note}</div>
                    </div>
                  </div>

                  {/* Address form hidden behind button */}
                  {!showAddressForm ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowAddressForm(true)}
                        className="btn-primary w-full justify-center"
                        data-testid="reveal-address-form"
                      >
                        <Truck size={14} /> Continuer vers la livraison →
                      </button>
                      {/* Pre-CTA reassurance checklist — 5 short facts
                          rendered as a compact 2-column grid to close the
                          last mental objections before the click. Distinct
                          from the accordion below, which digs into specs. */}
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-3 text-[12.5px] text-[color:var(--gv-ink)]/70" data-testid="shop-precta-checklist">
                        <li className="flex items-center gap-1.5"><span className="text-[color:var(--gv-pink)] font-semibold">✓</span> {c.precta.format} {selectedVariant?.size || c.precta.pending}</li>
                        <li className="flex items-center gap-1.5"><span className="text-[color:var(--gv-pink)] font-semibold">✓</span> {selectedVariant?.paper_spec?.weight_gsm ? c.precta.giclee_gsm(selectedVariant.paper_spec.weight_gsm) : c.precta.giclee_prem}</li>
                        <li className="flex items-center gap-1.5"><span className="text-[color:var(--gv-pink)] font-semibold">✓</span> {c.precta.ship_48}</li>
                        <li className="flex items-center gap-1.5"><span className="text-[color:var(--gv-pink)] font-semibold">✓</span> {c.precta.tube}</li>
                        <li className="flex items-center gap-1.5 sm:col-span-2"><span className="text-[color:var(--gv-pink)] font-semibold">✓</span> {c.precta.ideal}</li>
                      </ul>
                    </>
                  ) : (
                    <form onSubmit={handleQuote} className="space-y-5">
                      <div className="flex items-center justify-between">
                        <Label className="!mb-0">{c.ship_address}</Label>
                        <button type="button" onClick={() => setShowAddressForm(false)} className="text-[12px] text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-pink)]">{c.cancel}</button>
                      </div>
                      <AddressFields value={shipAddress} onChange={setShipAddress} />
                      <label className="flex items-center gap-2 text-[13px] text-[color:var(--gv-ink)]/80">
                        <input type="checkbox" checked={billingSame} onChange={(e) => setBillingSame(e.target.checked)} data-testid="billing-same-checkbox" />
                        {c.billing_same}
                      </label>
                      {!billingSame && <div className="bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] rounded-[5px] p-3 text-[12.5px] text-[color:var(--gv-ink)]/70">{c.billing_stripe}</div>}
                      {checkoutError && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-[5px] text-[13px]">{checkoutError}</div>}
                      <button type="submit" disabled={quoting || !selectedVariant} className="btn-primary w-full justify-center" data-testid="quote-submit">
                        {quoting ? <><Loader2 size={14} className="animate-spin" /> {c.computing_ship}</> : <>{c.compute_ship}</>}
                      </button>
                    </form>
                  )}

                  {/* Selling arguments — collapsed accordion. The pictos
                      cover the high-level trust; the details pane covers
                      the "convert-me" facts (paper spec, hanging, etc.) */}
                  <SellingArgumentsAccordion paperSpec={selectedVariant?.paper_spec} />

                  {/* Cross-sell + livraison offerte + bulk nudge — same
                      component reused on the review step (step 2) below. */}
                  <CheckoutCrossSell
                    productId={productId}
                    currentQuantity={quantity}
                    currentUnitCents={selectedVariant?.amount_cents || 0}
                    onIncrement={() => setQuantity((q) => Math.min(20, q + 1))}
                  />
                </div>
              )}

              {step === 2 && quote && (
                <div className="space-y-5" data-testid="checkout-step-2">
                  <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-4">
                    <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-3">{c.recap}</div>
                    <Row label={`${selectedVariant?.color || ""} ${selectedVariant?.size || ""} × ${quote.quantity}`.trim()} value={formatCents(quote.subtotal_cents, quote.currency)} />
                    <Row label={c.shipping} value={formatCents((quote.shipping_options.find((o) => o.method_uid === chosenMethod)?.price_cents) || 0, quote.currency)} />
                    <div className="pt-2 mt-2 border-t border-[color:var(--gv-line)] flex items-center justify-between font-display text-[18px]">
                      <span>{c.total}</span><span data-testid="checkout-total">{formatCents(quote.subtotal_cents + ((quote.shipping_options.find((o) => o.method_uid === chosenMethod)?.price_cents) || 0), quote.currency)}</span>
                    </div>
                  </div>
                  <div>
                    <Label>{c.ship_method}</Label>
                    <div className="space-y-2">
                      {quote.shipping_options.map((opt) => (
                        <label key={opt.method_uid}
                               className={`flex items-center justify-between p-3 border rounded-[5px] cursor-pointer transition-colors ${chosenMethod === opt.method_uid ? "border-[color:var(--gv-pink)] bg-[color:var(--gv-pink)]/5" : "border-[color:var(--gv-line)] hover:border-[color:var(--gv-ink)]/30"}`}
                               data-testid={`shipping-option-${opt.method_uid}`}>
                          <div className="flex items-center gap-3">
                            <input type="radio" name="shipping" checked={chosenMethod === opt.method_uid} onChange={() => setChosenMethod(opt.method_uid)} />
                            <div>
                              <div className="text-[14px] text-[color:var(--gv-ink)]">{opt.label}</div>
                              <div className="text-[12px] text-[color:var(--gv-ink)]/55">{opt.min_days && opt.max_days ? c.days(opt.min_days, opt.max_days) : opt.type}</div>
                            </div>
                          </div>
                          <div className="font-display text-[15px] text-[color:var(--gv-ink)]">{formatCents(opt.price_cents, quote.currency)}</div>
                        </label>
                      ))}
                    </div>
                  </div>
                  {checkoutError && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-[5px] text-[13px]">{checkoutError}</div>}
                  <div className="flex gap-3">
                    <button type="button" onClick={() => { setStep(1); setQuote(null); }} className="btn-secondary" data-testid="back-to-address">{c.edit_address}</button>
                    <button type="button" onClick={handlePay} disabled={paying || !chosenMethod} className="btn-primary flex-1 justify-center" data-testid="pay-submit">
                      {paying ? <><Loader2 size={14} className="animate-spin" /> {c.redirecting}</> : <>{c.pay_secure}</>}
                    </button>
                  </div>
                  {/* Same nudge block on the review step. We pass the
                      quote's shipped total (subtotal + shipping) so the
                      "still missing X €" line reflects reality. */}
                  <CheckoutCrossSell
                    productId={productId}
                    currentQuantity={quote.quantity}
                    currentUnitCents={selectedVariant?.amount_cents || 0}
                    onIncrement={() => { setQuantity((q) => Math.min(20, q + 1)); setStep(1); setQuote(null); }}
                  />
                </div>
              )}

              {step === 3 && (
                <div className="text-center py-8">
                  <Loader2 size={24} className="animate-spin mx-auto text-[color:var(--gv-pink)] mb-3" />
                  <div className="text-[14px] text-[color:var(--gv-ink)]/65">{c.stripe_redirect}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Upsell — mix : Même région · Best-seller · Nouveauté */}
      {upsells.length > 0 && (
        <section className="py-12 md:py-16 bg-white border-t border-[color:var(--gv-line)]" data-testid="upsell-section">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles size={18} className="text-[color:var(--gv-pink)]" />
              <h2 className="font-display text-2xl md:text-3xl text-[color:var(--gv-ink)]">{c.you_may_also_like}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {upsells.map((it) => (
                <Link key={it.product_id} to={`/shop/${it.product_id}`}
                      className="group relative block bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden hover:border-[color:var(--gv-pink)]/40 hover:shadow-md transition-all"
                      data-testid={`upsell-card-${it.product_id}`}>
                  {it._tag && (
                    <div className="absolute top-3 left-3 z-10 px-2 py-1 bg-[color:var(--gv-ink)] text-white text-[10px] uppercase tracking-widest rounded-[5px]" data-testid={`upsell-tag-${it._tag.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                      {it._tag}
                    </div>
                  )}
                  <div className="aspect-square bg-[color:var(--gv-mist)] flex items-center justify-center">
                    <img
                      src={`${API}${it.preview}${it.preview && it.preview.includes("?") ? "&" : "?"}w=400`}
                      alt={it.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="font-display text-[14px] text-[color:var(--gv-ink)] mb-1 group-hover:text-[color:var(--gv-pink)] transition-colors line-clamp-2">{it.title}</div>
                    <div className="text-[12px] text-[color:var(--gv-ink)]/65">{c.from_price} <span className="font-display text-[color:var(--gv-ink)]">{formatFromEUR(it.from_amount_cents)}</span></div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Netlinking croisé — Blog */}
            <div className="mt-8" data-testid="product-crosslinks">
              <Link to="/blog" className="group block bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)] rounded-[5px] p-5 hover:bg-[color:var(--gv-line)] transition-colors border border-[color:var(--gv-line)]" data-testid="product-crosslink-blog">
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-1.5">{c.explorer_eyebrow}</div>
                <div className="font-display text-[18px] mb-1 group-hover:text-[color:var(--gv-pink)] transition-colors">{c.explorer_title}</div>
                <div className="text-[12.5px] text-[color:var(--gv-ink)]/65">{c.explorer_sub}</div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Customer reviews — only rendered when the product has approved
          reviews (component returns null otherwise). */}
      <ProductReviewsBlock productId={productId} />

      {/* Mobile-only sticky bottom CTA. Scrolls the user right into the
          buy panel — the section id is set on the outer <section>. */}
      <StickyMobileCta
        show={step === 1 && !!selectedVariant}
        price={(() => {
          if (!selectedVariant?.amount_cents) return "";
          const tier = currentBulkTier(quantity);
          const base = selectedVariant.amount_cents * quantity;
          const finalCents = tier ? Math.round(base * (1 - tier.pct / 100)) : base;
          return formatFromEUR(finalCents);
        })()}
        ctaLabel={showAddressForm ? c.sticky_ship : c.sticky_add}
        totalLabel={c.total}
        onClick={() => {
          if (!showAddressForm) setShowAddressForm(true);
          const el = document.querySelector('[data-testid="checkout-step-1"]');
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      />
    </Layout>
  );
};

const Label = ({ children, className = "" }) => (
  <div className={`text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2 ${className}`}>{children}</div>
);

const FieldSelect = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label>{label}</Label>
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="gv-input w-full appearance-none pr-9" data-testid={testId} required>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40 pointer-events-none" />
    </div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-[13px] text-[color:var(--gv-ink)]/80 py-1">
    <span>{label}</span><span>{value}</span>
  </div>
);

/**
 * Bulk-order incentive — pulls the visitor toward multi-card orders from
 * the very first card selected. Renders the full discount ladder plus a
 * live upsell nudge ("Ajoutez 1 carte pour -15 %") and a one-click
 * increment button so unlocking the next tier stays frictionless. When
 * the max tier is reached we swap to an unlocked confirmation state.
 */
const BulkIncentive = ({ quantity, unitCents = 0, onIncrement }) => {
  const c = useCopy();
  const active = currentBulkTier(quantity);
  const next = nextBulkTier(quantity);
  const needed = next ? next.min - quantity : 0;

  // Compute the free-shipping progress. Uses the same discounted total
  // the "Total" line shows so the two numbers never contradict.
  const totalCents = (() => {
    if (!unitCents) return 0;
    const base = unitCents * quantity;
    return active ? Math.round(base * (1 - active.pct / 100)) : base;
  })();
  const missingCents = Math.max(0, FREE_SHIPPING_THRESHOLD_CENTS - totalCents);
  const freeShippingUnlocked = totalCents >= FREE_SHIPPING_THRESHOLD_CENTS && totalCents > 0;
  const progressPct = totalCents
    ? Math.min(100, Math.round((totalCents / FREE_SHIPPING_THRESHOLD_CENTS) * 100))
    : 0;
  const fmt = (c) => `${(c / 100).toFixed(2).replace(".", ",")} €`;

  return (
    <div
      className="p-3 bg-white border border-[color:var(--gv-line)] rounded-[5px] space-y-2.5"
      data-testid="bulk-incentive"
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)]">
        <Layers size={12} /> {c.bulk.start_collection}
      </div>

      {next ? (
        <div className="flex items-center justify-between gap-3 text-[13px]">
          <span className="text-[color:var(--gv-ink)]/80">
            {(() => {
              if (!unitCents) return null;
              const grossAtNext = unitCents * next.min;
              const netAtNext = Math.round(grossAtNext * (1 - next.pct / 100));
              const marginalGain = Math.max(0, (unitCents * needed) - (netAtNext - totalCents));
              return (
                <>
                  {c.bulk.add_cards(needed)} — <strong>{next.label}</strong>
                  {marginalGain > 0 && (
                    <> · <strong className="text-[color:var(--gv-pink)]">{c.bulk.save_amount(fmt(marginalGain))}</strong></>
                  )}
                </>
              );
            })() || <>{c.bulk.add_cards(needed)} — <strong>{next.label}</strong></>}
          </span>
          <button
            type="button"
            onClick={onIncrement}
            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-[4px] bg-[color:var(--gv-pink)] text-white text-[11.5px] font-medium hover:opacity-90"
            data-testid="bulk-incentive-add"
          >
            <TrendingUp size={11} /> {c.bulk.add1}
          </button>
        </div>
      ) : (
        <div className="text-[13px] text-green-700" data-testid="bulk-incentive-maxed">
          {c.bulk.max_unlocked(active?.label || "")}
        </div>
      )}

      {active && unitCents > 0 && (
        <div className="text-[12px] text-green-700 -mt-0.5" data-testid="bulk-savings-line">
          {c.bulk.already_saving(fmt(Math.round(unitCents * quantity * (active.pct / 100))))}
        </div>
      )}

      {/* Ladder — always visible so the visitor sees the full economic
          argument. Active tier gets a subtle background highlight. */}
      <div className="grid grid-cols-3 gap-1.5">
        {BULK_TIERS.slice().reverse().map((t) => {
          const isActive = active && t.min === active.min;
          return (
            <div
              key={t.min}
              className={`text-center px-2 py-2 rounded-[4px] border transition-colors ${
                isActive
                  ? "border-[color:var(--gv-pink)] bg-[color:var(--gv-pink)]/8"
                  : "border-[color:var(--gv-line)] bg-[color:var(--gv-mist)]/40"
              }`}
              data-testid={`bulk-tier-${t.min}`}
            >
              <div className="text-[11.5px] font-medium text-[color:var(--gv-ink)]">{t.pitch}</div>
              <div className={`text-[13px] font-display leading-tight ${isActive ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]/70"}`}>
                {t.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Free-shipping threshold nudge — kept below the ladder so the
          two incentives read left-to-right as a sequence rather than
          competing. Only shown when unit price is known. */}
      {unitCents > 0 && (
        <div className="pt-2.5 border-t border-[color:var(--gv-line)]" data-testid="free-shipping-progress">
          {freeShippingUnlocked ? (
            <div className="flex items-center gap-2 text-[13px] text-green-700">
              <Truck size={14} />
              <span><strong>{c.bulk.free_ship_unlocked}</strong></span>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-[12.5px] text-[color:var(--gv-ink)]/75 mb-1.5">
                <span className="inline-flex items-center gap-1.5"><Truck size={13} className="text-[color:var(--gv-pink)]" /> {c.bulk.free_ship_goal}</span>
                <span className="tabular-nums">{c.bulk.missing} <strong>{fmt(missingCents)}</strong></span>
              </div>
              <div className="h-1.5 bg-[color:var(--gv-mist)] rounded-full overflow-hidden">
                <div className="h-full bg-[color:var(--gv-pink)] transition-all" style={{ width: `${progressPct}%` }} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const AddressFields = ({ value, onChange }) => {
  const c = useCopy();
  const set = (k, v) => onChange({ ...value, [k]: v });
  const handleAutocomplete = (fields) => {
    onChange({ ...value, ...fields });
  };
  return (
    <div className="grid grid-cols-2 gap-3">
      <input className="gv-input" placeholder={c.address.firstName} required value={value.firstName} onChange={(e) => set("firstName", e.target.value)} data-testid="addr-firstName" />
      <input className="gv-input" placeholder={c.address.lastName} required value={value.lastName} onChange={(e) => set("lastName", e.target.value)} data-testid="addr-lastName" />
      <input className="gv-input col-span-2" placeholder={c.address.email} type="email" required value={value.email} onChange={(e) => set("email", e.target.value)} data-testid="addr-email" />
      <AddressAutocomplete onSelect={handleAutocomplete} />
      <input className="gv-input col-span-2" placeholder={c.address.line1} required value={value.addressLine1} onChange={(e) => set("addressLine1", e.target.value)} data-testid="addr-line1" />
      <input className="gv-input col-span-2" placeholder={c.address.line2} value={value.addressLine2} onChange={(e) => set("addressLine2", e.target.value)} data-testid="addr-line2" />
      <input className="gv-input" placeholder={c.address.city} required value={value.city} onChange={(e) => set("city", e.target.value)} data-testid="addr-city" />
      <input className="gv-input" placeholder={c.address.postCode} required value={value.postCode} onChange={(e) => set("postCode", e.target.value)} data-testid="addr-postcode" />
      <select className="gv-input" required value={value.country} onChange={(e) => set("country", e.target.value)} data-testid="addr-country">
        {COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
      </select>
      <input className="gv-input" placeholder={c.address.phone} value={value.phone} onChange={(e) => set("phone", e.target.value)} data-testid="addr-phone" />
    </div>
  );
};

/**
 * Below-the-fold accordion. Kept 3 sections deep on purpose — a longer
 * accordion signals "boring specs" and buries the CTA further down the
 * page. Copy is factual, not fluffy — credibility converts on
 * print-on-demand.
 */
const SellingArgumentsAccordion = ({ paperSpec }) => {
  const c = useCopy();
  const [open, setOpen] = useState("paper");
  const spec = paperSpec || {};
  const paperLine = [
    spec.paper_type || c.accordion.paper_type,
    spec.weight_gsm ? `${spec.weight_gsm} g/m²` : null,
    spec.finish || c.accordion.paper_matte,
  ].filter(Boolean).join(" · ");
  const whatsappHref =
    "https://wa.me/33660235665?text=" +
    encodeURIComponent("Bonjour, je viens de la boutique geoVINUM — j'aurais besoin d'aide sur ma commande.");
  const sections = [
    {
      key: "paper",
      icon: Leaf,
      title: c.accordion.paper,
      body: (
        <ul className="space-y-1.5 text-[13px] text-[color:var(--gv-ink)]/80 list-disc list-inside">
          <li><strong>{paperLine}</strong></li>
          {c.accordion.paper_bullets.map((b) => <li key={b}>{b}</li>)}
        </ul>
      ),
    },
    {
      key: "production",
      icon: Package,
      title: c.accordion.production,
      body: (
        <ul className="space-y-1.5 text-[13px] text-[color:var(--gv-ink)]/80 list-disc list-inside">
          {c.accordion.production_bullets.map((b, i) => (
            <li key={i}>{typeof b === "function" ? b() : b}</li>
          ))}
        </ul>
      ),
    },
    {
      key: "returns",
      icon: ShieldCheck,
      title: c.accordion.returns,
      body: (
        <ul className="space-y-1.5 text-[13px] text-[color:var(--gv-ink)]/80 list-disc list-inside">
          <li>{c.accordion.returns_bullets_pre} <strong>{c.accordion.returns_days}</strong> {c.accordion.returns_bullets_post}</li>
          <li>{c.accordion.returns_free}</li>
          <li>{c.accordion.returns_refund}</li>
          <li>
            {c.accordion.returns_sav}{" "}
            <a href="mailto:help@geovinum.io" className="text-[color:var(--gv-pink)] underline">help@geovinum.io</a>
            {" "}{c.accordion.returns_or}{" "}
            <a href={whatsappHref} target="_blank" rel="noreferrer" className="text-[color:var(--gv-pink)] underline" data-testid="sav-whatsapp">WhatsApp +33 6 60 23 56 65</a>
          </li>
        </ul>
      ),
    },
  ];
  return (
    <div className="mt-4 border-t border-[color:var(--gv-line)] pt-4 space-y-1" data-testid="selling-arguments">
      {sections.map(({ key, icon: Icon, title, body }) => {
        const isOpen = open === key;
        return (
          <div key={key} className="border-b border-[color:var(--gv-line)] last:border-b-0">
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : key)}
              className="flex items-center justify-between w-full py-3 text-left"
              data-testid={`selling-arg-toggle-${key}`}
            >
              <span className="flex items-center gap-2.5 text-[14px] font-medium text-[color:var(--gv-ink)]">
                <Icon size={15} className="text-[color:var(--gv-pink)]" /> {title}
              </span>
              <ChevronDown size={16} className={`text-[color:var(--gv-ink)]/50 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && <div className="pb-4">{body}</div>}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Sticky mobile CTA — floating bar with current price + primary action,
 * docked to viewport bottom on screens < lg. Hidden on desktop (the
 * right-column button always shows there) and once the checkout leaves
 * step 1 (so the address form owns its own space).
 */
const StickyMobileCta = ({ show, price, onClick, ctaLabel, totalLabel }) => {
  if (!show) return null;
  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-white/95 backdrop-blur border-t border-[color:var(--gv-line)] px-4 py-3 flex items-center gap-3"
      data-testid="shop-sticky-cta"
    >
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 leading-none mb-0.5">{totalLabel}</div>
        <div className="font-display text-[18px] text-[color:var(--gv-ink)] leading-none">{price || "—"}</div>
      </div>
      <button
        type="button"
        onClick={onClick}
        className="btn-primary shrink-0"
        data-testid="shop-sticky-cta-btn"
      >
        <Truck size={14} /> {ctaLabel}
      </button>
    </div>
  );
};

export default ShopProductPage;